import { useEffect } from 'react';
import { css } from '@emotion/react';
import { ReviewsApi, ReviewsQueries } from '@frontend/api-reviews';
import { useTranslation } from '@frontend/i18n';
import { useQuery } from '@frontend/react-query-helpers';
import { theme } from '@frontend/theme';
import { ErrorBadgeIcon, Heading, SpinningLoader, styles, Text } from '@frontend/design-system';
import { AddBookingLinkToBusinessProfile } from './AddBookingLinkToBusinessProfile';
import { AddToGoogleBusinessProfilePlaceholder } from './AddToGoogleBusinessProfilePlaceholder';
import { GoogleBusinessProfileNotVerifiedBanner } from './GoogleBusinessProfileNotVerifiedBanner';
import { InvalidTokenBanner } from './InvalidTokenBanner';

type GoogleAccountContentProps = {
  locationId: string;
  bookingSiteURL: string;
};

const SpinningLoaderContainer = () => (
  <div css={[styles.flexCenter, { marginTop: theme.spacing(2) }]}>
    <SpinningLoader size='medium' />
  </div>
);

const ErrorCard = () => {
  const { t } = useTranslation('scheduleGlobalSettings');
  return (
    <div css={errorCardStyles}>
      <ErrorBadgeIcon color='error' />
      <section>
        <Heading level={2}>{t('An error occurred while loading your settings.')}</Heading>
        <Text>{t('Try reloading the page. If the problem persists, contact Weave Support.')}</Text>
      </section>
    </div>
  );
};

export const GoogleAccountContent = ({ locationId, bookingSiteURL }: GoogleAccountContentProps) => {
  const { t } = useTranslation('scheduleGlobalSettings');
  const {
    data: mappings,
    isLoading: isLoadingMappings,
    isError: isErrorMappings,
    refetch: refetchMappings,
  } = ReviewsQueries.useGetMappings({
    queryKey: ['google-mappings', locationId],
    enabled: false,
  });

  const {
    data: integrationStatusData,
    isLoading: isLoadingIntegrationStatus,
    refetch: refetchIntegrationStatusData,
  } = useQuery({
    queryFn: () => ReviewsApi.getGoogleIntegrationStatus({ locationId: locationId, isGoogle: true }),
    queryKey: ['google-integration-status', locationId],
    enabled: false,
  });

  useEffect(() => {
    if (locationId) {
      refetchMappings();
      refetchIntegrationStatusData();
    }
  }, [locationId]);

  const mappingState = mappings?.googleMapping?.setupState?.toLowerCase();

  const hasGoogleAccountMappingSetup =
    mappings?.googleMapping && (mappingState === 'authsuccess' || mappingState === 'integrationsuccess');

  const hasValidGoogleAuthToken = integrationStatusData?.data?.google?.validToken;
  const isLocationVerified = integrationStatusData?.data?.google?.locationState?.isVerified;

  const shouldRenderAddBookingLinkToBusinessProfile = hasGoogleAccountMappingSetup && hasValidGoogleAuthToken;

  const hasInvalidToken = hasGoogleAccountMappingSetup && !hasValidGoogleAuthToken;

  if (isLoadingMappings || isLoadingIntegrationStatus) return <SpinningLoaderContainer />;
  if (isErrorMappings) return <ErrorCard />;

  return (
    <>
      <Heading level={3}>{t('Add to Google Business Profile')}</Heading>
      <Text>
        {t(
          'Add appointment booking directly to your Google Business Profile, enhancing accessibility and streamlining engagement. Connect now for effortless customer interaction.'
        )}
      </Text>
      {hasInvalidToken && <InvalidTokenBanner />}
      {shouldRenderAddBookingLinkToBusinessProfile && !isLocationVerified && <GoogleBusinessProfileNotVerifiedBanner />}
      {mappings?.googleMapping && shouldRenderAddBookingLinkToBusinessProfile ? (
        <AddBookingLinkToBusinessProfile
          googleMapping={mappings?.googleMapping}
          bookingSiteURL={bookingSiteURL}
          locationId={locationId}
          isLocationVerified={!!isLocationVerified}
        />
      ) : (
        <AddToGoogleBusinessProfilePlaceholder />
      )}
    </>
  );
};

const errorCardStyles = css({
  display: 'flex',
  alignItems: 'center',
  border: `solid 1px ${theme.colors.critical50}`,
  borderRadius: theme.borderRadius.small,
  background: theme.colors.critical10,
  marginTop: theme.spacing(4),
  padding: theme.spacing(3),
  gap: theme.spacing(2),
});
